// 261010 Epic 2 / P5c — CHÍNH SÁCH hiển thị bằng chứng HÌNH của phiếu "Duyệt B · AI xem ảnh thật" (thuần: không antd/router, jest chạy được).
// Luật: chỉ mở ảnh CÓ trong manifest đã gửi cho giám khảo (`snapshot.media`) · chỉ vẽ vùng khi BE trả vùng hợp lệ trong [0,1] — KHÔNG suy vị trí frame từ lưới / nhóm (Rev 1: không tự crop theo lưới đoán)
// · mốc giây luôn ghi là KẾ HOẠCH storyboard (không phải thời gian video đã quan sát; không có player giả) · FE không tự dựng URL ảnh, chỉ dùng `url` BE cấp và chỉ khi an toàn.
import type { MediaEvidence, MediaRegion, ReviewJudge, ReviewMediaItem, ReviewSnapshotMeta } from '../../types/filmStepReview';

const EPS = 1e-9;
const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const pct = (v: number): string => `${+(v * 100).toFixed(2)}%`;

export interface RegionStyle { left: string; top: string; width: string; height: string }
/** Vùng chuẩn hoá [0,1] gốc trên-trái, không rỗng, không tràn ra ngoài ảnh. */
export const isRegionValid = (r: unknown): r is MediaRegion => {
  if (!r || typeof r !== 'object') return false;
  const { x, y, w, h } = r as Record<string, unknown>;
  if (!num(x) || !num(y) || !num(w) || !num(h)) return false;
  return x >= 0 && y >= 0 && w > 0 && h > 0 && x + w <= 1 + EPS && y + h <= 1 + EPS;
};
export const regionStyle = (r: MediaRegion | null | undefined): RegionStyle | null =>
  (isRegionValid(r) ? { left: pct(r.x), top: pct(r.y), width: pct(r.w), height: pct(r.h) } : null);

/** `url` BE cấp chỉ được dùng khi là http(s) hoặc đường dẫn gốc (`/…`), không userinfo; loại `javascript:` / `data:` / protocol-relative. */
export function safeMediaUrl(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  if (u.startsWith('/')) return u.startsWith('//') ? null : u;
  if (!/^https?:\/\//i.test(u)) return null;
  const host = u.replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
  return host.includes('@') ? null : u;
}

export const frameLabel = (frameNo: number | undefined): string => (num(frameNo) && Number.isInteger(frameNo) && frameNo >= 1 ? `Frame ${frameNo}` : 'Cả sheet');
const sec = (n: number): string => String(+n.toFixed(2));
export const plannedRangeLabel = (startS: number, endS: number): string =>
  (num(startS) && num(endS) && endS >= startS ? `kế hoạch storyboard ${sec(startS)}–${sec(endS)} s` : 'kế hoạch storyboard (chưa rõ khoảng giây)');

/** URL mở ảnh: ưu tiên bản đã chuẩn hoá BE lưu (`storedUrl`), rơi về `sourceUrl`; mỗi cái phải qua `safeMediaUrl`. */
export const mediaSrc = (m: Pick<ReviewMediaItem, 'storedUrl' | 'sourceUrl'>): string | null => safeMediaUrl(m.storedUrl) ?? safeMediaUrl(m.sourceUrl);

export interface EvidenceView {
  mediaId: string; frameNo: number; frameLabel: string; rangeLabel: string; observation: string;
  media: ReviewMediaItem | null; src: string | null; region: MediaRegion | null; openable: boolean;
}
/** Mỗi bằng chứng hình → view. Mở được CHỈ khi `mediaId` thuộc manifest và URL an toàn; vùng chỉ giữ khi mở được VÀ hợp lệ. */
export function evidenceViews(evs: MediaEvidence[] | undefined, snapshot: ReviewSnapshotMeta | null | undefined): EvidenceView[] {
  return (evs ?? []).map((e) => {
    const media = snapshot?.media?.find((m) => m.mediaId === e.mediaId) ?? null;
    const src = media ? mediaSrc(media) : null;
    const openable = !!media && !!src;
    return {
      mediaId: e.mediaId, frameNo: e.frameNo, frameLabel: frameLabel(e.frameNo), rangeLabel: plannedRangeLabel(e.startS, e.endS), observation: e.observation ?? '',
      media, src: openable ? src : null, region: openable && isRegionValid(e.region) ? e.region : null, openable,
    };
  });
}

export interface MediaRow { mediaId: string; roleLabel: string; order: number; sizeLabel: string; shaShort: string; mime: string; src: string | null }
const sizeLabel = (b: unknown): string => {
  if (!num(b) || b < 0) return '—';
  if (b >= 1024 * 1024) return `${+(b / (1024 * 1024)).toFixed(1)} MiB`;
  return `${Math.round(b / 1024)} KiB`;
};
/** `label` của BE là chữ trong đề gửi giám khảo (tiếng Anh với tấm) ⇒ tấm hiện chữ cố định; ảnh tham chiếu hiện thêm nhãn BE (tên nhân vật / nền…). */
const roleLabel = (m: Pick<ReviewMediaItem, 'role' | 'label'>): string => {
  if (m.role === 'SHEET') return 'Tấm storyboard (ảnh gốc)';
  if (m.role === 'REF') return m.label ? `Ảnh tham chiếu · ${m.label}` : 'Ảnh tham chiếu';
  return m.role;
};
/** Ảnh đã gửi, theo thứ tự gửi (`order`). `shaShort` = hash bytes ĐÃ GỬI (`sha256`). */
export function mediaRows(snapshot: ReviewSnapshotMeta | null | undefined): MediaRow[] {
  return [...(snapshot?.media ?? [])].sort((a, b) => a.order - b.order).map((m) => ({
    mediaId: m.mediaId, roleLabel: roleLabel(m), order: m.order, sizeLabel: sizeLabel(m.bytes), shaShort: (m.sha256 ?? '').slice(0, 8), mime: m.mime, src: mediaSrc(m),
  }));
}

export interface CoverageSummary { pass: number; fail: number; notAssessable: number; total: number }
/** Ô phủ `(tiêu chí × frame)` của MỘT giám khảo; không có `coverage` (phiếu P3d) ⇒ null. */
export function coverageSummary(j: Pick<ReviewJudge, 'coverage'> | undefined): CoverageSummary | null {
  if (!j?.coverage) return null;
  const c = j.coverage;
  return {
    pass: c.filter((x) => x.result === 'PASS').length, fail: c.filter((x) => x.result === 'FAIL').length,
    notAssessable: c.filter((x) => x.result === 'NOT_ASSESSABLE').length, total: c.length,
  };
}
export const coverageLine = (s: CoverageSummary | null): string | null =>
  (s && s.total > 0 ? `Phủ ${s.total} ô (tiêu chí × frame): ${s.pass} đạt · ${s.fail} lỗi · ${s.notAssessable} không đủ quan sát` : null);
